import { ETypeNotification } from "@/modules/helpers/enum"
import {
  DRAFT_TYPES,
  parseAdditionalData,
  SUBMISSION_TYPES
} from "@/modules/helpers/utils/notification"
import { sendOpenLink } from "@/modules/helpers/utils/post-message"
import { Notification } from "@/modules/types"

// Notification type groups for outer app redirects
const OUTER_APP_REDIRECT_TYPES = new Set<ETypeNotification>([
  ETypeNotification.REDIRECT_OUTER_APP,
  ETypeNotification.REMIND_ATTACH_LINK_AFFILIATE,
  ETypeNotification.REMIND_AUTHORIZED_SHOWCASE
])

/**
 * Validates if a string is a valid internal path or URL
 */
const isValidDeepLink = (link: string): boolean => {
  if (!link || typeof link !== "string") {
    return false
  }

  const trimmedLink = link.trim()

  // Valid internal path
  if (trimmedLink.startsWith("/")) {
    return true
  }

  // Valid external URL
  try {
    const url = new URL(trimmedLink)
    return url.protocol === "http:" || url.protocol === "https:"
  } catch {
    return false
  }
}

/**
 * Sanitizes a deep link to prevent potential security issues
 */
const sanitizeDeepLink = (link: string): string | null => {
  if (!isValidDeepLink(link)) {
    console.warn("Invalid deep link detected:", link)
    return null
  }

  return link.trim()
}

export const redirectLinkNotification = (
  notification: Notification
): {
  to: string
  params?: Record<string, string>
  search?: Record<string, string | undefined>
} | null => {
  const { type } = notification

  if (!type) {
    return notification.id ? { to: "/notification/$id", params: { id: notification.id } } : null
  }

  const {
    deepLink,
    buttonText,
    campaignId,
    submissionId,
    conversationCode,
    otherUserId,
    shopName,
    showSkuIds,
    requestProductSamples
  } = parseAdditionalData(notification)

  // Handle submission-related notifications
  if (SUBMISSION_TYPES.has(type)) {
    return submissionId ? { to: "/manage", search: { submissionId } } : { to: "/manage" }
  }

  // Handle draft-related notifications
  if (DRAFT_TYPES.has(type)) {
    return submissionId
      ? { to: "/manage", search: { submissionId, showDraftsManager: "true" } }
      : { to: "/manage" }
  }

  // Handle message notifications
  if (type === ETypeNotification.SEND_MESSAGE) {
    return conversationCode
      ? {
          to: "/manage/chat/$code",
          params: { code: conversationCode },
          search: { shopName, otherUserId, submissionId }
        }
      : { to: "/manage" }
  }

  // Handle outer app redirects (external links)
  if (OUTER_APP_REDIRECT_TYPES.has(type)) {
    if (deepLink) {
      const sanitizedLink = sanitizeDeepLink(deepLink)
      if (sanitizedLink) {
        sendOpenLink(sanitizedLink)
      }
    }
    return null // No internal navigation needed
  }

  // Handle SKU update notifications
  if (type === ETypeNotification.UPDATE_SKU) {
    return campaignId
      ? {
          to: "/campaign/affiliate/$id/update-sku",
          params: { id: campaignId },
          search: {
            showSkuIds: showSkuIds?.join(","),
            requestProductSamplesId: requestProductSamples?.id?.toString()
          }
        }
      : { to: "/manage" }
  }

  // Handle new campaign notifications
  if (type === ETypeNotification.NEW_CAMPAIGN || type === ETypeNotification.INVITE_JOIN_CAMPAIGN) {
    return campaignId ? { to: "/campaign/affiliate/$id", params: { id: campaignId } } : { to: "/" }
  }

  // Handle internal app redirects
  if (type === ETypeNotification.REDIRECT_INTERNAL_APP) {
    if (deepLink) {
      const sanitizedLink = sanitizeDeepLink(deepLink)
      if (sanitizedLink) {
        return { to: sanitizedLink }
      }
    }
    return { to: "/" }
  }

  // Handle cast campaign payment notification (external link)
  if (type === ETypeNotification.CAST_CAMP_PAYMENT) {
    if (deepLink) {
      const sanitizedLink = sanitizeDeepLink(deepLink)
      if (sanitizedLink) {
        sendOpenLink(sanitizedLink)
      }
    }
    return null // No internal navigation needed
  }

  // Handle customize notification with deeplink
  if (type === ETypeNotification.CUSTOMIZE_NOTIFICATION) {
    if (deepLink) {
      const sanitizedLink = sanitizeDeepLink(deepLink)
      if (sanitizedLink) {
        return {
          to: "/notification/$id",
          params: { id: notification.id || "" },
          search: { deeplink: sanitizedLink, type, buttonText }
        }
      }
    }
    return notification.id
      ? { to: "/notification/$id", params: { id: notification.id } }
      : { to: "/" }
  }

  // Handle other notification types with details page
  if (
    type === ETypeNotification.DELIVERED ||
    type === ETypeNotification.REJECT_ACCEPTANCE ||
    type === ETypeNotification.ACCEPT_ACCEPTANCE ||
    type === ETypeNotification.CREATE_CONTRACT
  ) {
    return notification.id
      ? { to: "/notification/$id", params: { id: notification.id } }
      : { to: "/" }
  }

  // Default: redirect to notification detail page with optional deeplink
  if (deepLink) {
    return {
      to: "/notification/$id",
      params: { id: notification.id || "" },
      search: { deeplink: deepLink, type, buttonText }
    }
  }

  return notification.id
    ? { to: "/notification/$id", params: { id: notification.id } }
    : { to: "/" }
}
